<script lang="ts" setup>
import TextField from "~/components/form/TextField.vue";
import {toTypedSchema} from "@vee-validate/zod";
import * as z from "zod";
import {useForm} from "vee-validate";
import {PhArrowRight} from "@phosphor-icons/vue";
import {createErrorMap} from "#base-layer/utils/zod/zod-i18n-form-error-map";
import {promiseTimeout} from "@vueuse/core";
import {joinURL} from "ufo";
import {FetchError} from "ofetch";
import {object} from "zod";
import SelectField from "~/components/form/SelectField.vue";

const props = defineProps<{
  formId: string;
  authorName?: string;
  authorEmail?: string;
}>();

const {t} = useI18n();
const formMsg = ref<string | null>(null);
const formMsgTranslated = computed(() => {
  return formMsg.value != null ? t(formMsg.value) : "";
});
const form = ref<HTMLFormElement>();

const validationSchema = toTypedSchema(
  z.object({
    name: z.string().trim().min(1, {message: "label.required"}),
    email: z
      .string()
      .trim()
      .min(1, {message: "label.required"})
      .email({message: "label.form.invalid.email"}),
    message: z.string().trim().min(1, {message: "label.required"}),
    customFields: object({
      pageTitle: z.string().optional(),
      authorName: z.string().optional(),
      authorEmail: z.string().optional(),
      company: z.string().optional(),
      role: z.string().trim().min(1, {message: "label.required"}),
      industry: z.string().trim().min(1, {message: "label.required"}),
    }),
  }),
  {
    errorMap: createErrorMap(t),
  },
);
const pageTitle = inject<string>("pageTitle");
const {values, handleSubmit, isSubmitting, errors, resetForm} = useForm({
  validationSchema,
  initialValues: {
    name: "",
    email: "",
    message: "",
    customFields: {
      pageTitle: pageTitle || "",
      authorName: "",
      authorEmail: "",
      company: "",
      role: "",
      industry: "",
    },
  },
});

const onSuccess = () => {
  resetForm();
  formMsg.value = "label.form.state.submitted";
};
const onError = () => {
  formMsg.value = "label.form.state.error";
};

const dataLayer: Record<string, any>[] = import.meta.client ? (window.dataLayer ??= []) : [];
const formName = "Contact";
const runtimeConfig = useRuntimeConfig();

const onSubmit = handleSubmit(async (values) => {
  await promiseTimeout(500);

  if (props.authorName) {
    values.customFields.authorName = props.authorName;
  }
  if (props.authorEmail) {
    values.customFields.authorEmail = props.authorEmail;
  }

  const base = runtimeConfig.public.contact.api;
  const projectId = runtimeConfig.public.contact.somfyId;
  const api = joinURL(
    base,
    `/projects/${projectId}/contact-forms/${props.formId}/email`,
  );
  $fetch(api, {
    method: "POST",
    body: {
      ...values,
    },
  })
    .then(() => {
      dataLayer.push({
        event: "form_submit",
        form_name: formName,
        form_author_target: props.authorName,
      });
      onSuccess();
    })
    .catch((error: FetchError) => {
      dataLayer.push({
        event: "form_failure",
        form_name: formName,
        form_author_target: props.authorName,
        error_message: error.message,
      });
      onError();
    });
});
</script>

<style lang="scss">
.contact-form {
  .select-input select,
  .text-input textarea,
  .text-input input {
    @apply page-light:bg-white page-light:text-black;
  }
}
</style>

<template>
  <div class="contact-form page-dark:bg-black">
    <form
      class="form flex flex-col items-end gap-3 lg:gap-3"
      ref="form"
      @submit="onSubmit"
      v-if="formId">
      <div class="fields w-full flex flex-col gap-2">
        <TextField
          name="name"
          label=""
          :placeholder="$t('label.fullName')"
          :as-required="true"
          :label-visible="false" />
        <TextField
          name="customFields.company"
          label=""
          :placeholder="$t('label.company')"
          :label-visible="false" />
        <TextField
          name="email"
          label=""
          :placeholder="$t('label.emailAddress')"
          :as-required="true"
          :label-visible="false" />
        <SelectField
            name="customFields.role"
            label=""
            :placeholder="$t('label.role')"
            :as-required="true"
            :label-visible="false"
            :options="'role'" />
        <SelectField
          name="customFields.industry"
          label=""
          :placeholder="$t('label.industry')"
          :as-required="true"
          :label-visible="false"
          :options="'industries'" />
        <TextField
          name="message"
          label=""
          :placeholder="$t('label.message')"
          :as-required="true"
          :label-visible="false"
          element="textarea">
        </TextField>
        <div class="text-sunrise-200">{{ formMsgTranslated }}</div>
      </div>
      <div class="submission">
        <button
          class="link-with-icon page-dark:text-sunrise-100 page-light:bg-neutral-100 p-3 transition-colors duration-300 link-underline"
          type="submit"
          :disabled="isSubmitting">
          <span>{{
            isSubmitting ? $t("label.submitting") : $t("label.send")
          }}</span>
          <PhArrowRight />
        </button>
      </div>
    </form>
  </div>
</template>
